import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  Badge,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import { GripVertical, Save, X, ArrowUp, ArrowDown } from "lucide-react";
import { useMenuItemsStore } from "../../../stores/Assets/menuItems";

const OrderingModal = ({ isOpen, toggle, menuId, onOrderingUpdate }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOverItem, setDraggedOverItem] = useState(null);
  const {executeItemsOrdering} = useMenuItemsStore();
  const [initialOrdering, setInitialOrdering] = useState([]);
  useEffect(() => {
    if (isOpen && menuId) {
      fetchMenuItems();
    }
  }, [isOpen, menuId]);

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      // Fetch ALL items without pagination
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/menu/${menuId}/items?page=1&perPage=1000`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch menu items");
      }

      const data = await response.json();
    
      // Sort items by ordering field first, then by created_at as fallback
      const sortedItems = data.items.sort((a, b) => {
        const orderA = a.ordering || 0;
        const orderB = b.ordering || 0;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        // If ordering is the same, sort by created_at
        return new Date(a.created_at) - new Date(b.created_at);
      });
      
      // Set initial ordering with actual ordering values from database
      // If ordering is 0, null, or undefined, assign sequential ordering
      const itemsWithOrdering = sortedItems.map((item, index) => ({
        ...item,
        ordering: (item.ordering && item.ordering > 0) ? item.ordering : index + 1,
      }));
      
      setInitialOrdering(itemsWithOrdering);
      setMenuItems(itemsWithOrdering);
    } catch (error) {
      console.error("Error fetching menu items:", error);
      toast.error("Failed to load menu items");
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", (item.item_id || item.menu_item_id || 0).toString());
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDraggedOverItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (e, item) => {
    e.preventDefault();
    setDraggedOverItem(item);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDraggedOverItem(null);
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    
    const draggedId = draggedItem?.item_id || draggedItem?.menu_item_id;
    const targetId = targetItem?.item_id || targetItem?.menu_item_id;
    
    if (!draggedItem || draggedId === targetId) {
      return;
    }

    const draggedIndex = menuItems.findIndex(
      (item) => (item.item_id || item.menu_item_id) === draggedId
    );
    const targetIndex = menuItems.findIndex(
      (item) => (item.item_id || item.menu_item_id) === targetId
    );

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newItems = [...menuItems];
    const [draggedItemData] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, draggedItemData);

    setMenuItems(newItems);
    setDraggedOverItem(null);
  };

  const moveItem = (fromIndex, toIndex) => {
    const newItems = [...menuItems];
    const [movedItem] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, movedItem);
    setMenuItems(newItems);
  };

  const moveUp = (index) => {
    if (index > 0) {
      moveItem(index, index - 1);
    }
  };

  const moveDown = (index) => {
    if (index < menuItems.length - 1) {
      moveItem(index, index + 1);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const orderedData = menuItems.map((item, index) => ({
        menu_item_id: item.item_id || item.menu_item_id,
        ordering: index + 1,
      }));

      const response = await executeItemsOrdering(orderedData);
      if (response) {
        toast.success("Menu items reordered successfully!");
      }
      onOrderingUpdate && onOrderingUpdate();
      toggle();
    } catch (error) {
      console.error("Error updating ordering:", error);
      toast.error("Failed to update menu item ordering");
    } 
  };

  const getItemImage = (item) => {
    if (item.images && item.images.length > 0) {
      return `${item.images[0]}`;
    }
    if (item.item_images && item.item_images.length > 0) {
      return `${item.item_images[0].image_url}`;
    }
    return null;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md" scrollable>
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center gap-2">
          <GripVertical size={20} />
          Reorder Menu Items
        </div>
      </ModalHeader>
      
      <ModalBody style={{  overflowY: "auto" }}>
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">Loading menu items...</p>
          </div>
        ) : (
          <div className="space-y-0">
            {menuItems.map((item, index) => (
              <div key={item.item_id} className="w-100">
                <Card
                  className={`h-100 mb-0 ${
                    (draggedItem?.menu_item_id || draggedItem?.menu_item_id) === (item.menu_item_id || item.menu_item_id)
                      ? "opacity-50"
                      : (draggedOverItem?.menu_item_id || draggedOverItem?.menu_item_id) === (item.menu_item_id || item.menu_item_id)
                      ? "border-primary border-2"
                      : "hover-shadow"
                  }`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, item)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, item)}
                  style={{ cursor: "move" }}
                >
                  <CardBody className="p-1">
                    <div className="d-flex align-items-center gap-3">
                      <div className="d-flex align-items-center gap-2">
                        <GripVertical 
                          size={16} 
                          className="text-muted" 
                        />
                        <Badge 
                          color="primary" 
                          style={{ fontSize: "12px" }}
                        >
                          #{initialOrdering.find((initialItem) => initialItem.menu_item_id === item.menu_item_id)?.ordering}
                        </Badge>
                      </div>
                      
                      <div className="d-flex align-items-center gap-3 flex-grow-1">
                        {getItemImage(item) ? (
                          <img
                            src={getItemImage(item)}
                            alt={item.title}
                            className="rounded"
                            style={{ 
                              width: "50px", 
                              height: "50px", 
                              objectFit: "cover" 
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <div 
                            className="rounded bg-light d-flex align-items-center justify-content-center"
                            style={{ 
                              width: "50px", 
                              height: "50px"
                            }}
                          >
                            <span className="text-muted" style={{ fontSize: "10px" }}>No Image</span>
                          </div>
                        )}
                        
                        <div className="flex-grow-1">
                          <h6 className="mb-0" title={item.title}>
                            {item.title}
                          </h6>
                         
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            ))}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={saving}>
          <X size={16} className="me-1" />
          Cancel
        </Button>
        <Button color="primary" onClick={handleSave} disabled={saving || loading}>
          {saving ? (
            <>
              <Spinner size="sm" className="me-1" />
              Saving...
            </>
          ) : (
            <>
              <Save size={16} className="me-1" />
              Save Order
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default OrderingModal;
